// src/pages/HomePage.js
import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { featureGroupsForRole, featureHref } from '../config/features';
import useFeatureBadges from '../hooks/useFeatureBadges';
import LandingPage from './LandingPage';
import './HomePage.css';

/** Roles that predate the Brandstore migration; they keep storefront access only. */
const LEGACY_ROLES = ['COMPANY_ADMIN', 'COMPANY_EMPLOYEE'];

const HomePage = () => {
    const { user } = useAuth();
    const badges = useFeatureBadges(user?.role);

    // Signed out — the marketing landing page.
    if (!user) {
        return <LandingPage />;
    }

    const role = user.role;
    const groups = featureGroupsForRole(role);

    // No role we recognise, and nothing to show: send them somewhere real rather
    // than rendering an empty page. Not `/login` — Login.js bounces authenticated
    // users straight back to /dashboard, which would loop.
    if (groups.length === 0) {
        return <Navigate to="/products" replace />;
    }

    const greetingName = user.username || user.email || 'there';

    return (
        <div className="dashboard">
            <header className="dashboard__header">
                <h1 className="dashboard__title">Hi, {greetingName}</h1>
                <p className="dashboard__subtitle">
                    {LEGACY_ROLES.includes(role)
                        ? 'Your company store has moved to Brandstore. Browse the catalogue here.'
                        : 'Everything you can do, in one place.'}
                </p>
            </header>

            {groups.map(({ group, items }) => (
                <section className="dashboard__group" key={group}>
                    <h2 className="dashboard__group-label">{group}</h2>
                    <ul className="dashboard__grid">
                        {items.map(item => {
                            const count = item.badgeKey ? (badges[item.badgeKey] || 0) : 0;
                            return (
                                <li key={item.key}>
                                    <Link to={featureHref(item)} className="dashboard__card">
                                        <span className="dashboard__card-icon" aria-hidden="true">{item.icon}</span>
                                        <span className="dashboard__card-body">
                                            <span className="dashboard__card-title">
                                                {item.label}
                                                {count > 0 && (
                                                    <span className="dashboard__card-badge">
                                                        {count}
                                                        <span className="sr-only"> items need attention</span>
                                                    </span>
                                                )}
                                            </span>
                                            <span className="dashboard__card-desc">{item.description}</span>
                                        </span>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                </section>
            ))}
        </div>
    );
};

export default HomePage;
